import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

export interface Comment {
    id: string;
    comment: string;
    date_created: string;
    date_updated?: string;
    user_created: {
        id: string;
        email?: string;
        first_name?: string;
        last_name?: string;
        avatar?: { id: string } | null;
    };
    user_updated?: string;
    collection: string;
    item: string;
}

// Helper to build query string manually to match reference API format
// This avoids aggressive URL encoding that qs/axios might do
function buildQueryString(params: Record<string, any>): string {
    const parts: string[] = [];

    // Handle fields array
    if (params.fields && Array.isArray(params.fields)) {
        for (const field of params.fields) {
            parts.push(`fields[]=${encodeURIComponent(field)}`);
        }
    }

    // Handle sort
    if (params.sort) {
        parts.push(`sort=${encodeURIComponent(params.sort)}`);
    }

    // Handle aggregate
    if (params.aggregate) {
        for (const [key, value] of Object.entries(params.aggregate)) {
            if (Array.isArray(value)) {
                for (const v of value) {
                    parts.push(`aggregate[${key}][]=${encodeURIComponent(v)}`);
                }
            } else {
                parts.push(`aggregate[${key}]=${encodeURIComponent(value as string)}`);
            }
        }
    }

    // Handle filter
    if (params.filter) {
        parts.push(...buildFilterParams(params.filter, 'filter'));
    }

    return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function buildFilterParams(filter: Record<string, any>, prefix: string): string[] {
    const parts: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        const paramKey = `${prefix}[${key}]`;

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            parts.push(...buildFilterParams(value, paramKey));
        } else if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                const item = value[i];
                if (typeof item === 'object' && item !== null) {
                    parts.push(...buildFilterParams(item, `${paramKey}[${i}]`));
                } else {
                    parts.push(`${paramKey}[${i}]=${encodeURIComponent(item)}`);
                }
            }
        } else {
            parts.push(`${paramKey}=${encodeURIComponent(value)}`);
        }
    }

    return parts;
}

export function useComments(collection: string, itemId: string) {
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuthStore();

    const queryKey = ['comments', collection, itemId];

    const { data: commentsCount = 0 } = useQuery({
        queryKey: [...queryKey, 'count'],
        queryFn: async () => {
            const queryString = buildQueryString({
                filter: {
                    _and: [
                        { collection: { _eq: collection } },
                        { item: { _eq: itemId } },
                    ],
                },
                aggregate: {
                    count: ['id'],
                },
            });

            const response = await api.get<{ data: [{ count: { id: string } }] }>(`/comments${queryString}`);
            return Number(response.data[0].count.id);
        },
        enabled: !!collection && !!itemId,
    });

    const { data: comments = [], isLoading, error } = useQuery({
        queryKey: [...queryKey, 'list'],
        queryFn: async () => {
            const queryString = buildQueryString({
                filter: {
                    collection: { _eq: collection },
                    item: { _eq: itemId },
                },
                sort: '-date_created',
                fields: [
                    'id',
                    'comment',
                    'date_created',
                    'date_updated',
                    'user_created.id',
                    'user_created.email',
                    'user_created.first_name',
                    'user_created.last_name',
                    'user_created.avatar.id',
                ],
            });

            const response = await api.get<{ data: Comment[] }>(`/comments${queryString}`);
            return response.data;
        },
        enabled: !!collection && !!itemId,
    });

    const createComment = useMutation({
        mutationFn: async (comment: string) => {
            const response = await api.post<{ data: Comment }>('/comments', {
                collection,
                item: itemId,
                comment,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const updateComment = useMutation({
        mutationFn: async ({ id, comment }: { id: string; comment: string }) => {
            const response = await api.patch<{ data: Comment }>(`/comments/${id}`, {
                comment,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const deleteComment = useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/comments/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    return {
        comments,
        commentsCount,
        isLoading,
        error,
        createComment,
        updateComment,
        deleteComment,
        currentUser,
    };
}
